import { getDbSupabase } from './supabase';
import { formatSupabaseError } from './formatSupabaseError';
import type {
  DemSkuMappingEntry,
  DemSkuMappingGroup,
  DemSkuMappingRow,
  DemTemplate,
  DemTemplateConfig,
} from '../types/dem';

const TEMPLATE_SELECT = 'name,config,updated_at';

export function createEmptyTemplateConfig(name: string): DemTemplateConfig {
  return {
    meta: { group_name: name, version: 1 },
    identification: { keywords: [name.toLowerCase()], file_pattern: '*.pdf' },
    template: { fields: {} },
    memory: { customers: {}, items: {} },
  };
}

function rowToTemplate(row: Record<string, unknown>): DemTemplate {
  const configRaw = row.config;
  const config =
    configRaw && typeof configRaw === 'object' && !Array.isArray(configRaw)
      ? (configRaw as DemTemplateConfig)
      : createEmptyTemplateConfig(String(row.name ?? ''));

  return {
    name: String(row.name ?? ''),
    config,
    updated_at: row.updated_at != null ? String(row.updated_at) : null,
  };
}

export function normalizeMappingEntries(
  value: DemSkuMappingEntry[] | DemSkuMappingEntry | string | undefined,
): DemSkuMappingEntry[] {
  if (value == null || value === '') return [];
  if (Array.isArray(value)) {
    return value.map((entry) => ({
      code: String(entry.code ?? '').trim(),
      factor: String(entry.factor ?? '1').trim() || '1',
      last_used: entry.last_used != null ? String(entry.last_used) : undefined,
      uom: entry.uom != null ? String(entry.uom) : undefined,
    }));
  }
  if (typeof value === 'object') {
    return [
      {
        code: String(value.code ?? '').trim(),
        factor: String(value.factor ?? '1').trim() || '1',
        last_used: value.last_used != null ? String(value.last_used) : undefined,
        uom: value.uom != null ? String(value.uom) : undefined,
      },
    ];
  }
  return [{ code: String(value).trim(), factor: '1' }];
}

function mappingPairKey(pdfKey: string, sysCode: string): string {
  return `${pdfKey.trim().toLowerCase()}\0${sysCode.trim().toLowerCase()}`;
}

export function flattenItemMappings(
  itemsMemory: Record<string, DemSkuMappingEntry[] | DemSkuMappingEntry | string> | undefined,
  descriptions: Record<string, string>,
): DemSkuMappingRow[] {
  const memory = itemsMemory ?? {};
  const rows: DemSkuMappingRow[] = [];
  const sysCodeCounts: Record<string, number> = {};
  const pdfSysPairCounts: Record<string, number> = {};

  for (const [pdfKey, raw] of Object.entries(memory)) {
    for (const entry of normalizeMappingEntries(raw)) {
      const sysCode = entry.code;
      if (sysCode) {
        sysCodeCounts[sysCode] = (sysCodeCounts[sysCode] ?? 0) + 1;
        const pair = mappingPairKey(pdfKey, sysCode);
        pdfSysPairCounts[pair] = (pdfSysPairCounts[pair] ?? 0) + 1;
      }
      rows.push({
        pdfKey,
        sysCode,
        factor: entry.factor || '1',
        uom: entry.uom?.trim() ?? '',
        description: descriptions[sysCode] ?? (sysCode ? 'Unknown SKU' : ''),
        lastUsed: entry.last_used?.trim() || '-',
        unknownSku: Boolean(sysCode && !descriptions[sysCode]),
        legacyDate: false,
        duplicateSysCode: false,
        duplicatePdfText: false,
      });
    }
  }

  for (const row of rows) {
    row.duplicateSysCode = Boolean(row.sysCode && (sysCodeCounts[row.sysCode] ?? 0) > 1);
    row.duplicatePdfText = Boolean(
      row.sysCode && (pdfSysPairCounts[mappingPairKey(row.pdfKey, row.sysCode)] ?? 0) > 1,
    );
    row.legacyDate = row.lastUsed === '-' || row.lastUsed === '0000-00-00';
  }

  rows.sort((a, b) => {
    const bySys = a.sysCode.localeCompare(b.sysCode, undefined, { sensitivity: 'base' });
    if (bySys !== 0) return bySys;
    return a.pdfKey.localeCompare(b.pdfKey, undefined, { sensitivity: 'base' });
  });

  return rows;
}

export async function fetchDemTemplateNames(search?: string): Promise<string[]> {
  const db = getDbSupabase('dem');
  const { data, error } = await db.from('templates').select('name').order('name', { ascending: true });
  if (error) throw new Error(formatSupabaseError(error));

  const needle = search?.trim().toLowerCase() ?? '';
  return (data ?? [])
    .map((row) => String((row as { name?: string }).name ?? ''))
    .filter((name) => name && (!needle || name.toLowerCase().includes(needle)));
}

export async function fetchDemTemplate(name: string): Promise<DemTemplate | null> {
  const db = getDbSupabase('dem');
  const { data, error } = await db
    .from('templates')
    .select(TEMPLATE_SELECT)
    .eq('name', name)
    .maybeSingle();

  if (error) throw new Error(formatSupabaseError(error));
  if (!data) return null;
  return rowToTemplate(data as Record<string, unknown>);
}

export function groupSkuMappingsByPdfKey(rows: DemSkuMappingRow[]): DemSkuMappingGroup[] {
  const map = new Map<string, DemSkuMappingRow[]>();

  for (const row of rows) {
    const list = map.get(row.pdfKey) ?? [];
    list.push(row);
    map.set(row.pdfKey, list);
  }

  return [...map.entries()]
    .sort(([a], [b]) => a.localeCompare(b, undefined, { sensitivity: 'base' }))
    .map(([pdfKey, groupRows]) => ({ pdfKey, rows: groupRows }));
}

export async function fetchDemTemplates(search?: string): Promise<DemTemplate[]> {
  const db = getDbSupabase('dem');
  const { data, error } = await db.from('templates').select(TEMPLATE_SELECT).order('name', { ascending: true });
  if (error) throw new Error(formatSupabaseError(error));

  const needle = search?.trim().toLowerCase() ?? '';
  return (data ?? [])
    .map((row) => rowToTemplate(row as Record<string, unknown>))
    .filter((tmpl) => !needle || tmpl.name.toLowerCase().includes(needle));
}

export async function createDemTemplate(name: string): Promise<DemTemplate> {
  const trimmed = name.trim();
  if (!trimmed) throw new Error('Template name is required.');

  const existing = await fetchDemTemplate(trimmed);
  if (existing) throw new Error('Template name already exists.');

  const config = createEmptyTemplateConfig(trimmed);
  const db = getDbSupabase('dem');
  const { data, error } = await db
    .from('templates')
    .insert({
      name: trimmed,
      config,
      updated_at: new Date().toISOString(),
    })
    .select(TEMPLATE_SELECT)
    .maybeSingle();

  if (error) throw new Error(formatSupabaseError(error));
  if (!data) {
    throw new Error('Create returned no rows — check write permissions on templates.');
  }
  return rowToTemplate(data as Record<string, unknown>);
}

export async function deleteDemTemplate(name: string): Promise<void> {
  const db = getDbSupabase('dem');
  const { error } = await db.from('templates').delete().eq('name', name);
  if (error) throw new Error(formatSupabaseError(error));
}

export async function saveDemTemplateConfig(name: string, config: DemTemplateConfig): Promise<DemTemplate> {
  const db = getDbSupabase('dem');
  const { data, error } = await db
    .from('templates')
    .upsert(
      {
        name,
        config,
        updated_at: new Date().toISOString(),
      },
      { onConflict: 'name' },
    )
    .select(TEMPLATE_SELECT)
    .maybeSingle();

  if (error) throw new Error(formatSupabaseError(error));
  if (!data) {
    throw new Error('Save returned no rows — check write permissions on templates.');
  }
  return rowToTemplate(data as Record<string, unknown>);
}

export async function fetchItemDescriptions(codes: string[]): Promise<Record<string, string>> {
  const details = await fetchItemDetails(codes);
  const descriptions: Record<string, string> = {};
  for (const [code, detail] of Object.entries(details)) {
    descriptions[code] = detail.description;
  }
  return descriptions;
}

export type DemItemDetail = {
  description: string;
  uom: string;
  packing: string;
  is_blocked: boolean;
  base_price: number;
  /** Matching lid item code(s) from PowerBI item_list.no_3 */
  lid_code: string;
};

/** Split items.lid_code (no_3); empty / "NO LID" → []. */
export function parseMatchingLidCodes(lidCode: string | null | undefined): string[] {
  const raw = lidCode?.trim() ?? '';
  if (!raw || raw.toUpperCase() === 'NO LID') return [];
  return [
    ...new Set(
      raw
        .toUpperCase()
        .split(/[,/]+/)
        .map((s) => s.trim())
        .filter(Boolean),
    ),
  ];
}

export async function fetchItemDetails(codes: string[]): Promise<Record<string, DemItemDetail>> {
  const unique = [...new Set(codes.map((c) => c.trim()).filter(Boolean))];
  if (unique.length === 0) return {};

  const db = getDbSupabase('dem');
  const details: Record<string, DemItemDetail> = {};
  const chunkSize = 100;

  for (let i = 0; i < unique.length; i += chunkSize) {
    const chunk = unique.slice(i, i + chunkSize);
    const { data, error } = await db
      .from('items')
      .select('code,description,uom,packing,is_blocked,lid_code')
      .in('code', chunk);
    if (error) throw new Error(formatSupabaseError(error));
    for (const row of data ?? []) {
      const code = String((row as { code?: string }).code ?? '');
      if (!code) continue;
      details[code] = {
        description: String((row as { description?: string }).description ?? ''),
        uom: String((row as { uom?: string }).uom ?? ''),
        packing: String((row as { packing?: string }).packing ?? ''),
        is_blocked: Boolean((row as { is_blocked?: boolean }).is_blocked),
        // items 表无 base_price 列；Nav 兜底价来自 prices 表，此处仅保留类型兼容
        base_price: 0,
        lid_code: String((row as { lid_code?: string | null }).lid_code ?? '').trim(),
      };
    }
  }

  return details;
}

export function upsertItemMapping(
  config: DemTemplateConfig,
  pdfKey: string,
  sysCode: string,
  factor: string,
): DemTemplateConfig {
  const trimmedPdf = pdfKey.trim();
  const trimmedSys = sysCode.trim();
  const trimmedFactor = factor.trim() || '1';
  const today = new Date().toISOString().slice(0, 10);

  const next = structuredClone(config);
  if (!next.memory) next.memory = {};
  if (!next.memory.items) next.memory.items = {};

  const existing = normalizeMappingEntries(next.memory.items[trimmedPdf]);
  let updated = false;
  for (const entry of existing) {
    if (entry.code === trimmedSys) {
      entry.factor = trimmedFactor;
      entry.last_used = today;
      updated = true;
      break;
    }
  }
  if (!updated) {
    existing.push({ code: trimmedSys, factor: trimmedFactor, last_used: today });
  }

  // Drop exact code duplicates that accumulate from failed edits.
  const seen = new Set<string>();
  const deduped: DemSkuMappingEntry[] = [];
  for (const entry of existing) {
    const key = `${entry.code.trim().toLowerCase()}|${(entry.uom ?? '').trim().toLowerCase()}`;
    if (seen.has(key)) continue;
    seen.add(key);
    deduped.push(entry);
  }

  next.memory.items[trimmedPdf] = deduped;
  return next;
}

/** Edit an existing mapping in place — remove the previous row, then upsert the new values. */
export function replaceItemMapping(
  config: DemTemplateConfig,
  previous: { pdfKey: string; sysCode: string; factor: string; uom?: string },
  next: { pdfKey: string; sysCode: string; factor: string },
): DemTemplateConfig {
  const withoutPrevious = removeItemMapping(
    config,
    previous.pdfKey,
    previous.sysCode,
    previous.factor,
    previous.uom,
  );
  return upsertItemMapping(withoutPrevious, next.pdfKey, next.sysCode, next.factor);
}

export function removeItemMapping(
  config: DemTemplateConfig,
  pdfKey: string,
  sysCode: string,
  factor: string,
  uom?: string,
): DemTemplateConfig {
  const trimmedPdf = pdfKey.trim();
  const trimmedSys = sysCode.trim();
  const trimmedFactor = factor.trim() || '1';
  const trimmedUom = (uom ?? '').trim();

  const next = structuredClone(config);
  const items = next.memory?.items;
  if (!items || !(trimmedPdf in items)) return next;

  const entries = normalizeMappingEntries(items[trimmedPdf]);
  const index = entries.findIndex((entry) => {
    if (entry.code !== trimmedSys) return false;
    if ((entry.factor || '1') !== trimmedFactor) return false;
    // When uom is provided, match exactly (including empty ↔ empty) to remove one duplicate.
    if (uom !== undefined) return (entry.uom?.trim() ?? '') === trimmedUom;
    return true;
  });
  if (index >= 0) entries.splice(index, 1);

  if (entries.length === 0) {
    delete items[trimmedPdf];
  } else {
    items[trimmedPdf] = entries;
  }

  return next;
}

export async function loadTemplateMappings(name: string): Promise<{
  template: DemTemplate;
  rows: DemSkuMappingRow[];
}> {
  const template = await fetchDemTemplate(name);
  if (!template) throw new Error('Template not found.');

  const provisional = flattenItemMappings(template.config.memory?.items, {});
  const codes = provisional.map((row) => row.sysCode).filter(Boolean);
  const descriptions = await fetchItemDescriptions(codes);
  const rows = flattenItemMappings(template.config.memory?.items, descriptions);

  return { template, rows };
}

export async function fetchItemDescription(code: string): Promise<string | null> {
  const trimmed = code.trim();
  if (!trimmed) return null;
  const map = await fetchItemDescriptions([trimmed]);
  return map[trimmed] ?? null;
}
